import { validJsonValue, validShoot } from '../features/studio/studio-command';
import type { HistoryRepeat } from '../api/history';
const key = 'steos:history:repeat';
function ownerId() {
  return (window as unknown as { BotServerMe?: { user?: { id?: number } } }).BotServerMe?.user?.id;
}
function positive(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}
/** Только данные формы: повтор не создаёт заказ, не выбирает прежнюю цену и не оживляет истёкшие файлы. */
export function normalizeHistoryRepeat(raw: unknown): HistoryRepeat | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.screen !== 'string' || !/^[a-z][a-z0-9_]{0,40}$/.test(value.screen)) return null;
  const result: HistoryRepeat = { screen: value.screen };
  for (const field of ['app_id', 'template_version', 'voice_id', 'project_id', 'avatar_id'] as const) {
    if (value[field] !== undefined) {
      if (!positive(value[field])) return null;
      result[field] = value[field];
    }
  }
  if (value.text !== undefined) {
    if (typeof value.text !== 'string' || value.text.length > 20000) return null;
    result.text = value.text;
  }
  if (value.inputs !== undefined) {
    if (!value.inputs || typeof value.inputs !== 'object' || Array.isArray(value.inputs)) return null;
    const entries = Object.entries(value.inputs);
    if (entries.length > 80) return null;
    result.inputs = {};
    for (const [name, input] of entries) {
      if (!/^[a-z][a-z0-9_]{0,60}$/.test(name) || ['consent_accepted', 'consent_version'].includes(name)) continue;
      if (['photo', 'video', 'shoot', 'pshoot'].includes(value.screen) && name === 'shoot' && validShoot(input, value.screen))
        result.inputs[name] = { ...input };
      else if (typeof input === 'string' && input.length <= 20000) result.inputs[name] = input;
      else if (typeof input === 'boolean' || (typeof input === 'number' && Number.isFinite(input))) result.inputs[name] = String(input);
      // Объекты и массивы полей с JSON-значением (docs/59): форма сама решит по value_encoding, брать ли их.
      else if (input && typeof input === 'object' && name !== 'shoot' && validJsonValue(input) && JSON.stringify(input).length <= 64 * 1024)
        result.inputs[name] = JSON.parse(JSON.stringify(input));
    }
  }
  return result;
}
export function historyRepeatRoute(entry: HistoryRepeat): 'tts' | 'projects' | 'studio' | 'clone' {
  if (entry.screen === 'clone') return 'clone';
  if (entry.screen === 'script') return 'projects';
  // Переозвучка и распознавание — инструменты Студии («Сменить голос в записи», «Текст»), во вкладке «Озвучка» только каталог
  if (entry.screen === 'tts') return 'tts';
  return 'studio';
}
export function readHistoryRepeat(route?: 'tts' | 'projects' | 'studio' | 'clone', expectedOwner?: number): HistoryRepeat | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
    const owner = expectedOwner ?? ownerId();
    if (!saved || !positive(owner) || saved.owner !== owner) return null;
    const entry = normalizeHistoryRepeat(saved.entry);
    return entry && (!route || historyRepeatRoute(entry) === route) ? entry : null;
  } catch {
    return null;
  }
}
export function openHistoryRepeat(raw: unknown): boolean {
  const entry = normalizeHistoryRepeat(raw),
    owner = ownerId();
  if (!entry || !positive(owner)) return false;
  sessionStorage.setItem(key, JSON.stringify({ owner, entry }));
  sessionStorage.removeItem('steos:studio:profile-entry');
  location.hash = '#' + historyRepeatRoute(entry);
  return true;
}
export function installHistoryRepeatBridge() {
  const listener = (event: Event) => {
    openHistoryRepeat((event as CustomEvent<unknown>).detail);
  };
  window.addEventListener('steos:history-repeat', listener);
  return () => window.removeEventListener('steos:history-repeat', listener);
}

export function clearHistoryRepeat() {
  sessionStorage.removeItem(key);
}
